/**
 * The files a turn produced — what `execute_python` wrote and the engine attached to the reply
 * (`turn_complete.attachments`: `{ contentId, fileName, fileType, url }`) — in the shape the
 * transcript renders, so the "Files from the run" row shows the moment the turn settles rather
 * than after a reload. PURE, alias-free (the test runner resolves none): tested in
 * `tests/producedAttachments.test.ts`.
 */
export interface ProducedAttachment {
  _id: string;
  contentType: 'file' | 'pdf';
  content: { url: string; fileName: string; fileType?: string };
}

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export function parseProducedAttachments(raw: unknown): ProducedAttachment[] {
  if (!Array.isArray(raw)) return [];
  const out: ProducedAttachment[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const url = asString(record.url);
    const id = asString(record.contentId) || asString(record._id) || url;
    if (!url || seen.has(id)) continue;
    seen.add(id);
    const fileName = asString(record.fileName) || url.split('/').pop() || 'file';
    const fileType =
      asString(record.fileType).toLowerCase() || fileName.split('.').pop()?.toLowerCase() || '';
    out.push({
      _id: id,
      contentType: fileType === 'pdf' ? 'pdf' : 'file',
      content: { url, fileName, ...(fileType ? { fileType } : {}) },
    });
  }
  return out;
}
